import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import proposalsService from '../../services/proposals.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import { CADENCE_LABEL, type Proposal } from '../../types/proposals';
import { readerLocale } from '../../i18n/language';

/**
 * "Proposals for you" (0070). A product an Account Executive or a campaign
 * offered this client. Factual only — no countdown, no urgency copy: the
 * expiry is the real offer window. Renders nothing when there are no open
 * proposals, so it never claims anything it does not know.
 */

function suggestionLine(p: Proposal): string | null {
  if (!p.suggested_amount) return null;
  const amount = formatMoney(p.suggested_amount, p.currency_code);
  return p.suggested_cadence
    ? `Suggested: ${amount} ${CADENCE_LABEL[p.suggested_cadence]}`
    : `Suggested: ${amount}`;
}

function ProposalRow({
  proposal,
  busy,
  onReview,
  onDecline,
}: Readonly<{
  proposal: Proposal;
  busy: boolean;
  onReview: (p: Proposal) => void;
  onDecline: (p: Proposal) => void;
}>): JSX.Element {
  const suggestion = suggestionLine(proposal);
  return (
    <li className="py-4" style={{ borderTop: `1px solid ${colors.borderSoft}` }}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>
            {proposal.product_name}
          </p>
          <p className="text-xs mt-0.5" style={{ color: colors.muted }}>
            {proposal.proposed_by_name
              ? `From your Account Executive, ${proposal.proposed_by_name}`
              : 'Available to you'}
            {' · open until '}
            {new Date(proposal.expires_at).toLocaleDateString(readerLocale())}
          </p>
          {proposal.message && (
            <p className="text-sm mt-2" style={{ color: colors.inkSoft }}>
              {proposal.message}
            </p>
          )}
          {suggestion && (
            <p className="text-xs mt-1" style={{ color: colors.muted }}>
              {suggestion}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            disabled={busy}
            onClick={() => onReview(proposal)}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold"
            style={{ background: colors.ink, color: colors.surface }}
          >
            {proposal.has_active_contract ? 'Make a contribution →' : 'Review and sign →'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecline(proposal)}
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ color: colors.inkSoft, border: `1px solid ${colors.border}` }}
          >
            Not now
          </button>
        </div>
      </div>
    </li>
  );
}

export default function ProposalsCard(): JSX.Element | null {
  const navigate = useNavigate();
  const [items, setItems] = useState<Proposal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await proposalsService.listMine());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load your proposals.'));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function review(p: Proposal): Promise<void> {
    if (!p.has_active_contract) {
      // The product page runs the PPM acknowledgement and every contract gate,
      // then accepts this proposal once the contract has started.
      navigate(`/client/products/${p.product_id}?proposal=${p.id}`);
      return;
    }
    setBusyId(p.id);
    try {
      await proposalsService.accept(p.id, null);
      navigate('/client/fund');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not open this proposal. Please try again.'));
      setBusyId(null);
    }
  }

  async function decline(p: Proposal): Promise<void> {
    setBusyId(p.id);
    try {
      await proposalsService.decline(p.id);
      setItems((current) => (current ?? []).filter((x) => x.id !== p.id));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not update this proposal. Please try again.'));
    } finally {
      setBusyId(null);
    }
  }

  if (error === null && (items === null || items.length === 0)) return null;

  return (
    <section
      className="rounded-2xl p-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <p className="text-base font-semibold" style={{ color: colors.ink }}>
        Proposals for you
      </p>
      {error && (
        <div className="mt-3 text-sm flex items-center gap-3" role="alert" style={{ color: colors.danger }}>
          <span>{error}</span>
          <button type="button" onClick={() => { void load(); }} className="underline">
            Retry
          </button>
        </div>
      )}
      {items && items.length > 0 && (
        <ul className="mt-2" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((p) => (
            <ProposalRow
              key={p.id}
              proposal={p}
              busy={busyId === p.id}
              onReview={(x) => { void review(x); }}
              onDecline={(x) => { void decline(x); }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
